import { useRef, useState } from 'react';

// I gesti del visore delle foto, come su WhatsApp (richiesta del 04/10/2026:
// «dover chiudere le foto solo spingendo X anziché poterle tirare giù»):
// - trascinare in giù chiude, con lo sfondo che sfuma;
// - trascinare di lato passa all'allegato vicino;
// - due dita ingrandiscono, un dito sposta la foto ingrandita;
// - doppio tocco ingrandisce lì (o torna intera), un tocco mostra o nasconde le barre.
export const SOGLIA_CHIUSURA = 110;      // px trascinati in giù per chiudere
export const VELOCITA_CHIUSURA = 0.6;    // oppure px/ms, per una spinta veloce
export const SOGLIA_SCORRI = 60;
const DOPPIO_TOCCO_MS = 280;
const MASSIMO = 5;
const DOPPIO = 2.5;

const distanza = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const centro = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const limita = (v, min, max) => Math.min(max, Math.max(min, v));

// La scala nuova tenendo fermo il punto `m` (rispetto al centro dell'area).
export function zoomSu(vista, scala, m) {
  const s = limita(scala, 1, MASSIMO);
  if (s === 1) return { s: 1, x: 0, y: 0 };
  const r = s / vista.s;
  return { s, x: m.x - (m.x - vista.x) * r, y: m.y - (m.y - vista.y) * r };
}

export default function useGestiFoto({ attivo, onClose, onScorri, onTocco }) {
  const [vista, setVista] = useState({ s: 1, x: 0, y: 0 });
  const [giu, setGiu] = useState(0);
  const [inMovimento, setInMovimento] = useState(false);
  const dita = useRef(new Map());
  const gesto = useRef(null);
  const ultimoTocco = useRef(null);
  const timerTocco = useRef(null);

  const azzera = () => { setVista({ s: 1, x: 0, y: 0 }); setGiu(0); };
  const relativo = (e, el) => {
    const r = el.getBoundingClientRect();
    return { x: e.clientX - (r.left + r.width / 2), y: e.clientY - (r.top + r.height / 2) };
  };

  const tocco = (e, el) => {
    const adesso = Date.now();
    const p = relativo(e, el);
    const prima = ultimoTocco.current;
    if (prima && adesso - prima.t < DOPPIO_TOCCO_MS && Math.abs(prima.x - p.x) < 40 && Math.abs(prima.y - p.y) < 40) {
      clearTimeout(timerTocco.current);
      ultimoTocco.current = null;
      setVista(v => (v.s > 1 ? { s: 1, x: 0, y: 0 } : zoomSu(v, DOPPIO, p)));
      return;
    }
    ultimoTocco.current = { t: adesso, ...p };
    clearTimeout(timerTocco.current);
    timerTocco.current = setTimeout(() => { ultimoTocco.current = null; onTocco?.(); }, DOPPIO_TOCCO_MS);
  };

  const gesti = !attivo ? {} : {
    onPointerDown: e => {
      try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (_) { /* jsdom, vecchi browser */ }
      dita.current.set(e.pointerId ?? 0, { x: e.clientX, y: e.clientY });
      const punti = [...dita.current.values()];
      setInMovimento(true);
      if (punti.length >= 2) {
        gesto.current = { tipo: 'pizzico', d0: distanza(punti[0], punti[1]) || 1, m0: centro(punti[0], punti[1]), vista0: vista };
      } else {
        gesto.current = { tipo: null, x: e.clientX, y: e.clientY, t: Date.now(), vista0: vista };
      }
    },
    onPointerMove: e => {
      if (!dita.current.has(e.pointerId ?? 0)) return;
      dita.current.set(e.pointerId ?? 0, { x: e.clientX, y: e.clientY });
      const g = gesto.current;
      if (!g) return;
      const punti = [...dita.current.values()];
      if (g.tipo === 'pizzico' && punti.length >= 2) {
        const el = e.currentTarget.getBoundingClientRect();
        const c = { x: el.left + el.width / 2, y: el.top + el.height / 2 };
        const m = centro(punti[0], punti[1]);
        const base = zoomSu(g.vista0, g.vista0.s * (distanza(punti[0], punti[1]) / g.d0), { x: g.m0.x - c.x, y: g.m0.y - c.y });
        // Il punto fra le dita segue anche lo spostamento delle dita.
        setVista(base.s === 1 ? base : { ...base, x: base.x + (m.x - g.m0.x), y: base.y + (m.y - g.m0.y) });
        return;
      }
      const dx = e.clientX - g.x;
      const dy = e.clientY - g.y;
      if (g.vista0.s > 1) {
        g.tipo = 'sposta';
        setVista({ ...g.vista0, x: g.vista0.x + dx, y: g.vista0.y + dy });
        return;
      }
      if (!g.tipo && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
        g.tipo = Math.abs(dy) > Math.abs(dx) && dy > 0 ? 'chiudi' : 'scorri';
      }
      if (g.tipo === 'chiudi') setGiu(Math.max(0, dy));
    },
    onPointerUp: e => {
      const el = e.currentTarget;
      dita.current.delete(e.pointerId ?? 0);
      const g = gesto.current;
      if (dita.current.size > 0) {
        // Da due dita a una: si continua spostando, senza salti.
        const [resto] = dita.current.values();
        gesto.current = { tipo: 'sposta', x: resto.x, y: resto.y, t: Date.now(), vista0: vista };
        return;
      }
      gesto.current = null;
      setInMovimento(false);
      if (!g) return;
      const dx = e.clientX - g.x;
      const dy = e.clientY - g.y;
      if (g.tipo === 'pizzico') {
        if (vista.s < 1.05) setVista({ s: 1, x: 0, y: 0 });
      } else if (g.tipo === 'chiudi') {
        const velocita = dy / Math.max(1, Date.now() - g.t);
        if (dy > SOGLIA_CHIUSURA || velocita > VELOCITA_CHIUSURA) onClose();
        else setGiu(0);
      } else if (g.tipo === 'scorri') {
        if (dx > SOGLIA_SCORRI) onScorri?.(-1);
        if (dx < -SOGLIA_SCORRI) onScorri?.(1);
      } else if (!g.tipo && Math.abs(dx) < 10 && Math.abs(dy) < 10) {
        tocco(e, el);
      }
    },
    onPointerCancel: e => {
      dita.current.delete(e.pointerId ?? 0);
      if (dita.current.size === 0) { gesto.current = null; setInMovimento(false); setGiu(0); }
    },
  };

  return { vista, setVista, giu, inMovimento, gesti, azzera };
}
